import { useMemo, useState } from 'react'

import { Button, DateInput, ErrorState, Field, inputClass } from '@/components/ui'
import { useCreateProject } from '@/features/projects/useProjects'
import { useTeams } from '@/features/teams/useTeams'

export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateProject()
  /**
   * Teams come from the API rather than a list in the client.
   *
   * This dialog previously offered the old flattened workstreams — so it showed "Business
   * Intelligence" after the team was renamed to EDEE, and worse, it sent `workstream`, which
   * the API no longer reads for team membership. Every project created through it landed as
   * Unassigned no matter what was chosen.
   *
   * Reading the live list means a category Travis adds appears here immediately, and a rename
   * shows up here too, with nothing to keep in step by hand.
   */
  const { data: teamData } = useTeams()
  // Memoised because `?? []` builds a new array every render, which would defeat the
  // useMemo below it — the same mistake eslint caught three times in the pilot.
  const teams = useMemo(() => teamData?.teams ?? [], [teamData])

  const [form, setForm] = useState({
    name: '',
    description: '',
    team: 'unassigned',
    category_id: '',
    owner_name: '',
    target_date: '',
  })

  const categories = useMemo(
    () => teams.find((team) => team.team === form.team)?.categories ?? [],
    [teams, form.team],
  )

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = () => {
    create.mutate(
      {
        name: form.name,
        description: form.description,
        team: form.team,
        // A category belongs to one team, so an empty choice must go as null rather than "".
        category_id: form.category_id || null,
        owner_name: form.owner_name,
        // The API takes a date or null; an empty string is neither.
        target_date: form.target_date || null,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New project"
      >
        <h2 className="mb-4 font-heading text-2xl text-fg">New project</h2>

        {create.error ? (
          <div className="mb-4">
            <ErrorState error={create.error} />
          </div>
        ) : null}

        <div className="space-y-3">
          <Field label="Name">
            <input className={inputClass} value={form.name} onChange={set('name')} autoFocus />
          </Field>
          <Field label="Description">
            <textarea
              className={inputClass}
              rows={3}
              value={form.description}
              onChange={set('description')}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Team">
              <select
                className={inputClass}
                value={form.team}
                onChange={(event) =>
                  // Changing team clears the category rather than carrying another team's
                  // across, which the API would reject anyway.
                  setForm((current) => ({
                    ...current,
                    team: event.target.value,
                    category_id: '',
                  }))
                }
              >
                <option value="unassigned">Unassigned</option>
                {teams
                  .filter((team) => team.team !== 'unassigned')
                  .map((team) => (
                    <option key={team.team} value={team.team}>
                      {team.label}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Owner">
              <input className={inputClass} value={form.owner_name} onChange={set('owner_name')} />
            </Field>
          </div>

          {/* Only shown when the chosen team actually subdivides. An empty category dropdown
              is a question with no answers. */}
          {categories.length > 0 ? (
            <Field label="Category" hint="Optional — a project can sit under the team itself.">
              <select className={inputClass} value={form.category_id} onChange={set('category_id')}>
                <option value="">No category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <Field label="Target date">
            <DateInput
              value={form.target_date}
              onChange={(value) => set('target_date')({ target: { value } })}
            />
          </Field>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </div>
      </div>
    </div>
  )
}
